import { HandymanPricePage } from '@/index/service-category/handyman/HandymanPricePage'
import { PageAnalytics } from '@/commons/PageAnalytics'
import { getArticleDateTime } from 'utils/article-date-time'
import { loadHandymanCategorySettings, loadHandymanPriceList, formatPrice, priceRange } from 'utils/handyman-price-data'
import { buildHandymanPriceFaqs } from 'utils/faq'
import { handymanMarketRates } from 'data/handyman-market-rates'
import {
  ELECTRICIAN_PRICE_PAGE,
  HANDYMAN_HUB,
  HANDYMAN_PRICE_PAGE,
  PLUMBER_HUB,
  handymanJobPages,
  jobPagePath,
  jobPagesForHub,
} from 'data/handyman-job-pages'

// Owns "handyman singapore price list" and "cheap handyman singapore".
// The hub shows only a short list of popular prices and links here, so the two
// URLs do not compete for the same query.

// Hand-set: bump when the prices or the market comparison are re-checked.
const LAST_REVIEWED = '2026-09-23T08:00:00+08:00'

export async function getStaticProps() {
  const { year } = getArticleDateTime()
  const handymanPriceList = await loadHandymanPriceList('Singapore')
  const requestCategorySettings = await loadHandymanCategorySettings('Singapore')

  return {
    props: { handymanPriceList, requestCategorySettings, year },
    revalidate: 259200, // In seconds
  }
}

const priceFactors = [
  { heading: 'The job itself', text: 'A quick fix such as replacing a door handle takes a fraction of the time of painting a room, and prices reflect the time and skill involved.' },
  { heading: 'How many jobs you book in one visit', text: 'Putting several small jobs into one visit avoids paying for separate trips, which is where many households overspend.' },
  { heading: 'Materials', text: 'Jobs listed as supply and install include the part. For install-only jobs, you can buy the part yourself or have the technician supply it at additional cost, agreed before work starts.' },
  { heading: 'Transport and call-out fees', text: 'Some providers quote a low job price and add a separate transport or call-out fee. Always compare the total, not the headline rate.' },
]

const cheapTips = (maxFee) => [
  { heading: 'Compare fixed prices, not hourly rates', text: 'A fixed per-job price cannot overrun. Hourly quotes are hard to compare and can grow if the job takes longer than expected.' },
  { heading: 'Check for call-out fees', text: `Some Singapore handyman companies add a transport or call-out fee of up to $${maxFee} on top of the job price. Ask for the total before you book.` },
  { heading: 'Bundle small jobs', text: 'List every small job in the house, such as a loose handle, a curtain rod and a picture to hang, and book them in one visit.' },
  { heading: 'Buy the part yourself for install-only jobs', text: 'For curtain rods, TV brackets, fans and locks, buying the item yourself lets you choose the price point.' },
  { heading: 'Use first-booking and referral rewards', text: 'StringsSG gives up to 20% off a first booking, loyalty points on every service and referral rewards for friends and family.' },
]

const relatedLinks = [
  ...jobPagesForHub(HANDYMAN_HUB).map((slug) => ({ href: jobPagePath(slug), label: handymanJobPages[slug].breadcrumbTitle })),
  { href: HANDYMAN_HUB, label: 'Handyman Services Singapore', desc: 'All handyman services and booking' },
  { href: ELECTRICIAN_PRICE_PAGE, label: 'Electrician Price List', desc: 'Lighting, sockets, DB box and more' },
  { href: PLUMBER_HUB, label: 'Plumber Services Singapore', desc: 'Leaks, taps, toilets and water heaters' },
]

export default function Page({ handymanPriceList, requestCategorySettings, year }) {
  const items = [...handymanPriceList].sort((a, b) => a.serviceCost - b.serviceCost)
  const range = priceRange(items)
  const low = formatPrice(range.low)

  const price = (name) => {
    const item = handymanPriceList.find((i) => i.itemName === name)
    return item ? formatPrice(item.serviceCost) : null
  }
  const faqs = buildHandymanPriceFaqs(price, { low, high: formatPrice(range.high) })

  return (
    <>
      <HandymanPricePage
        path={HANDYMAN_PRICE_PAGE}
        label="Handyman"
        title={`Handyman Price List Singapore (${year}) | From ${low}`}
        description={`Handyman price list for Singapore: ${items.length} fixed-price jobs from ${low}, typical market rates and tips to find a cheap handyman. Same-day slots.`}
        h1={`Handyman Price List Singapore (${year})`}
        items={items}
        handymanPriceList={handymanPriceList}
        requestCategorySettings={requestCategorySettings}
        marketRates={handymanMarketRates}
        faqs={faqs}
        cheapTips={cheapTips}
        priceFactors={priceFactors}
        relatedLinks={relatedLinks}
        lastReviewed={LAST_REVIEWED}
        image="handyman/handyman-singapore.jpg"
      />
      <PageAnalytics />
    </>
  )
}
